import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppointmentSlot } from '@utils/api/aboboApiTypes';
import AppointmentPicker from '../common/AppointmentPicker';
import { useBookingContext } from '@contexts/BookingContext';
import { useBranding, useClient } from '@utils/api';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';

interface AppointmentSelectionStepProps {
  isSingleServiceFlow: boolean;
}

const AppointmentSelectionStep = ({ isSingleServiceFlow }: AppointmentSelectionStepProps) => {
  const { branding } = useBranding();
  const { client } = useClient();
  const { t } = useTranslation();
  const naturalLanguageInputRef = useRef<HTMLParagraphElement>(null);
  const { bookingData, updateBookingData, goForward, clickWrapShown, setClickWrapShown } = useBookingContext();

  // Show clickwrap if in single service flow and not already shown
  const showClickWrap = isSingleServiceFlow && !clickWrapShown;

    // Timezone calculation
  const timeZoneDiff = useMemo(() => {
    if (!client) return 0;
    const date = new Date();
    const offset = date.getTimezoneOffset() / -60;
    return offset - client.timeZoneOffset;
  }, [client]);

  const timeZoneMessage = () => {
    if (timeZoneDiff === 0) return '';
    if (timeZoneDiff < 0) return t('booking.timeZoneAhead', {
      count: Math.abs(timeZoneDiff), 
      clinicName: branding?.name
    });
    if (timeZoneDiff > 0) return t('booking.timeZoneBehind', {
      count: timeZoneDiff, 
      clinicName: branding?.name
    });
  };

  const handleSlotSelect = (slot: AppointmentSlot) => {
    // Mark clickwrap as shown when user proceeds
    if (showClickWrap) {
      setClickWrapShown(true);
    }

    updateBookingData({
      appointmentsAttributes: [{
        ...bookingData.appointmentsAttributes[0],
        scheduledAt: slot.time,
        operatorId: slot.operatorIds[0],
      }]
    });
    goForward('patientInformation');
  };

  return (
    <div className="pt-4 pb-2">
      <Separator aria-hidden="true" role="separator"/>
      <p ref={naturalLanguageInputRef} className="font-medium pt-2 pb-2"> 
        {t('booking.secondStepTitle')} 
      </p>
      
      {timeZoneDiff !== 0 && (
        <Alert className="flex flex-col items-center justify-center text-center pb-2 mb-4">
          <AlertTitle>{t('booking.timeZoneDiff')}</AlertTitle>
          <AlertDescription>
            <div>{timeZoneMessage()}</div>
            <div>{t('booking.timeZoneInfo')}</div>
          </AlertDescription>
        </Alert>
      )}

      <div>
        <AppointmentPicker
          serviceId={bookingData.appointmentsAttributes[0]?.serviceId}
          onChange={handleSlotSelect}
        />
      </div>

      {showClickWrap && (
        <>
          <Separator aria-hidden="true" className="my-4"/>
          <p className="text-center text-sm pt-3">
            {t('booking.footer.confirm')}
            <a
              className="text-teal-700 hover:underline"
              target="_blank"
              href={`https://${client?.slug}.myclinically.com/privacy`}
              aria-label={t('aria.terms')}
            >
              {t('booking.footer.terms')}
            </a>
            {" "}{t('booking.footer.and')}{" "}
            <a
              className="text-teal-700 hover:underline"
              target="_blank"
              href={`https://${client?.slug}.myclinically.com/privacy`}
              aria-label={t('aria.privacy')}
            >
              {t('booking.footer.privacy')}
            </a>
          </p>
        </>
      )}
    </div>
  );
};

export default AppointmentSelectionStep;
